import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';

import { AppMaterialModule } from './app.material.module';
import { FlexLayoutModule } from '@angular/flex-layout';
import { NgxWebstorageModule } from 'ngx-webstorage';
import { FileUploadModule } from 'modules/fileupload/fileupload.module';
import { RecaptchaModule } from "ng-recaptcha";

import { AppRoutingModule } from 'routes/app-routing.module';
import { AuthenticatedGuard, UnsavedChangesGuard, UnsavedChangesService } from 'routes/routes.guard';

// Components
import { AppComponent } from './app.component';
import { AttachmentsComponent } from 'app/attachments/attachments.component';
import { HomeComponent } from 'app/home/home.component';
import { InstructionsComponent } from 'app/instructions/instructions.component';
import { LoginComponent } from 'app/login/login.component';
import { NominationFormComponent } from 'app/nominationform/nominationform.component';
import { RootComponent } from 'app/root/root.component';
import { ManageDesigneesComponent } from 'app/designees/designees.component';
import { ManageSchoolGroupsComponent } from 'app/schoolgroups/schoolgroups.component';
import { ManageWindowsComponent } from 'app/windows/windows.component';
import { NomineesComponent } from 'app/nominees/nominees.component';
import { ReportsComponent } from 'app/reports/reports.component';
import { SchoolSelectionComponent } from 'app/school/school.component';
import { RsvpComponent } from 'app/rsvp/rsvp.component';

// Dialog components
import { CheckboxDialogComponent } from 'dialogs/checkbox/checkbox.dialogcomponent';
import { ConfirmationDialogComponent } from 'dialogs/confirmation/confirmation.dialogcomponent';
import { CountdownDialogComponent } from 'dialogs/countdown/countdown.dialogcomponent';
import { InputBoxDialogComponent } from 'dialogs/inputbox/inputbox.dialogcomponent';
import { LoginBoxDialogComponent } from 'dialogs/loginbox/loginbox.dialogcomponent';
import { MessageBoxDialogComponent } from 'dialogs/messagebox/messagebox.dialogcomponent';
import { NomineeSearchDialogComponent } from 'dialogs/nomineesearch/nomineesearch.dialogcomponent';
import { NomineeInputDialogComponent } from 'dialogs/nomineeinput/nomineeinput.dialogcomponent';
import { OptionBoxDialogComponent } from 'dialogs/optionbox/optionbox.dialogcomponent';
import { ReportOptionsDialogComponent } from 'dialogs/reportoptions/reportoptions.dialogcomponent';
import { SelectBoxDialogComponent } from 'dialogs/selectbox/selectbox.dialogcomponent';
import { TextNoteDialogComponent } from 'dialogs/textnote/textnote.dialogcomponent';
import { UserSearchDialogComponent } from 'dialogs/usersearch/usersearch.dialogcomponent';
import { JudgeAddDialogComponent } from 'dialogs/judgeadd/judgeadd.dialogcomponent';
import { JudgeEmailDialogComponent } from 'dialogs/judgeemail/judgeemail.dialogcomponent';
import { JudgeSpreadsheetDialogComponent } from 'dialogs/judgespreadsheet/judgespreadsheet.dialogcomponent';
import { JudgeConfirmDialogComponent } from 'dialogs/judgeconfirm/judgeconfirm.dialogcomponent';
import { TieBreakerDialogComponent } from 'dialogs/tiebreaker/tiebreaker.dialogcomponent';
import { TieOverrideDialogComponent } from 'dialogs/tieoverride/tieoverride.dialogcomponent';
import { JudgeSelectDialogComponent } from 'dialogs/judgeselect/judgeselect.dialogcomponent';
import { JudgeDivideDialogComponent } from 'dialogs/judgedivide/judgedivide.dialogcomponent';

// UI components
import { AttachmentPanelComponent } from 'app/attachmentpanel/attachmentpanel.component';
import { FormLabelComponent } from 'ui/formlabel/formlabel.component';
import { FormPanelComponent } from 'app/formpanel/formpanel.component';
import { JCheckboxComponent } from 'ui/j-checkbox/jcheckbox.component';
import { NavMenuComponent } from 'ui/navmenu/navmenu.component';
import { UserInfoComponent } from 'ui/userinfo/userinfo.component';

// Service Components
import { SplashDialogComponent } from 'services/splash/splash.service';

// Directives
import { MobileTapDirective } from 'directives/MobileTap/mobiletap.directive';
import { DisablerControlDirective } from 'directives/disabler.directive';

// Pipes
import { AgePipe } from 'pipes/age.pipe';
import { CodeFilterPipe } from 'pipes/codefilter.pipe';
import { DateFnsPipe } from 'pipes/datefns.pipe';
import { FileFilterPipe } from 'pipes/filefilter.pipe';
import { FileSizePipe } from 'pipes/filesize.pipe';
import { NomineePipe } from 'pipes/nominee.pipe';
import { NomineeTypePipe } from 'pipes/nomineetype.pipe';
import { RoleTextPipe } from 'pipes/roletext.pipe';
import { SafeHTMLPipe } from 'pipes/safehtml.pipe';
import { SchoolNamePipe } from 'pipes/schoolname.pipe';
import { SchoolYearPipe } from 'pipes/schoolyear.pipe';
import { TextUnitPipe } from 'pipes/textunit.pipe';
import { TitleCasePipe } from 'pipes/titlecase.pipe';
import { WindowTypePipe } from 'pipes/windowtype.pipe';
import { YesNoPipe } from 'pipes/yesno.pipe';
import { FormTemplatePipe } from 'pipes/formtemplate.pipe';
import { DevToolsComponent } from './devtools/devtools.component';
import { JudgeToolComponent } from './judgetool/judgetool.component';
import { JudgeNomineesComponent } from './judgenominees/judgenominees.component';
import { JudgeFormComponent } from './judgeform/judgeform.component';
import { FinalizeNomineesComponent } from './finalizenominees/finalizenominees.component';
import { StatusComponent } from './status/status.component';
import { SchooleligibilityComponent } from './schooleligibility/schooleligibility.component';
import { PublicnominateComponent } from './publicnominate/publicnominate.component';
import { PublicattachmentpanelComponent } from './publicattachmentpanel/publicattachmentpanel.component';

@NgModule({
    declarations: [
        // App components
        AppComponent,
        AttachmentsComponent,
        HomeComponent,
        InstructionsComponent,
        LoginComponent,
        ManageDesigneesComponent,
        ManageSchoolGroupsComponent,
        ManageWindowsComponent,
        NominationFormComponent,
        NomineesComponent,
        ReportsComponent,
        RootComponent,
        SchoolSelectionComponent,
        RsvpComponent,

        // UI components
        AttachmentPanelComponent,
        FormLabelComponent,
        FormPanelComponent,
        JCheckboxComponent,
        NavMenuComponent,
        UserInfoComponent,

        // Service components
        SplashDialogComponent,

        // Dialog Components
        CheckboxDialogComponent,
        ConfirmationDialogComponent,
        CountdownDialogComponent,
        InputBoxDialogComponent,
        LoginBoxDialogComponent,
        MessageBoxDialogComponent,
        NomineeSearchDialogComponent,
        NomineeInputDialogComponent,
        OptionBoxDialogComponent,
        ReportOptionsDialogComponent,
        SelectBoxDialogComponent,
        TextNoteDialogComponent,
        UserSearchDialogComponent,
        JudgeAddDialogComponent,
        JudgeEmailDialogComponent,
        JudgeSpreadsheetDialogComponent,
        JudgeConfirmDialogComponent,
        TieBreakerDialogComponent,
        TieOverrideDialogComponent,
        JudgeSelectDialogComponent,
        JudgeDivideDialogComponent,

        // Pipes
        AgePipe,
        CodeFilterPipe,
        DateFnsPipe,
        FileFilterPipe,
        FileSizePipe,
        NomineePipe,
        NomineeTypePipe,
        RoleTextPipe,
        SafeHTMLPipe,
        SchoolNamePipe,
        SchoolYearPipe,
        TextUnitPipe,
        TitleCasePipe,
        WindowTypePipe,
        YesNoPipe,
        FormTemplatePipe,

        // Directives
        DisablerControlDirective,
        MobileTapDirective,
        DevToolsComponent,
        JudgeToolComponent,
        JudgeNomineesComponent,
        JudgeFormComponent,
        FinalizeNomineesComponent,
        StatusComponent,
        SchooleligibilityComponent,
        PublicnominateComponent,
        PublicattachmentpanelComponent
    ],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        FormsModule,
        ReactiveFormsModule,
        HttpClientModule,
        AppMaterialModule,
        FlexLayoutModule,
        AppRoutingModule,
        FileUploadModule,
        NgxWebstorageModule.forRoot(),
        RecaptchaModule
    ],
    providers: [
        AuthenticatedGuard, AgePipe, DateFnsPipe, DecimalPipe, FileSizePipe,
        NomineeTypePipe, RoleTextPipe, SchoolYearPipe, TextUnitPipe, TitleCasePipe, WindowTypePipe,
        UnsavedChangesGuard, UnsavedChangesService, YesNoPipe, FormTemplatePipe
    ],
    entryComponents: [
        SplashDialogComponent, CheckboxDialogComponent, ConfirmationDialogComponent, CountdownDialogComponent,
        InputBoxDialogComponent, LoginBoxDialogComponent, MessageBoxDialogComponent, NomineeSearchDialogComponent, NomineeInputDialogComponent,
        OptionBoxDialogComponent, ReportOptionsDialogComponent,
        SelectBoxDialogComponent, TextNoteDialogComponent, UserSearchDialogComponent, TieBreakerDialogComponent, TieOverrideDialogComponent,
        JudgeAddDialogComponent, JudgeEmailDialogComponent, JudgeSpreadsheetDialogComponent, JudgeConfirmDialogComponent, JudgeSelectDialogComponent,
        JudgeDivideDialogComponent],
    bootstrap: [AppComponent]
})
export class AppModule { }
